// src/service/Hooks/useSecureDomain.js
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSelectedDomain from './useSelectedDomain';
import { domainUUIDManager } from '../domainUUIDManager';

export const useSecureDomain = () => {
  const { domainUUID } = useParams();
  const navigate = useNavigate();
  const selectedDomain = useSelectedDomain();
  const [secureDomainId, setSecureDomainId] = useState(null);
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(true);

  // Convertir l'UUID en ID réel
  const getRealDomainId = useCallback((uuid) => {
    return domainUUIDManager.getDomainIdFromUUID(uuid);
  }, []);

  // Convertir l'ID réel en UUID
  const getDomainUUID = useCallback((domainId) => {
    return domainUUIDManager.getUUIDFromDomainId(domainId);
  }, []);

  // Valider et récupérer le domaine sécurisé
  useEffect(() => {
    const validateAndSetDomain = async () => {
      setLoading(true);
      
      try {
        if (domainUUID) {
          // Valider l'UUID du domaine
          const realDomainId = getRealDomainId(domainUUID);
          
          if (realDomainId) {
            setSecureDomainId(realDomainId);
            setIsValid(true);
          } else {
            setIsValid(false);
            // console.warn('Invalid domain UUID:', domainUUID);
          }
        } else if (selectedDomain?.id) {
          // Générer un UUID pour le domaine sélectionné
          const uuid = getDomainUUID(selectedDomain.id);
          setSecureDomainId(selectedDomain.id);
          setIsValid(true);
        } else {
          setIsValid(false);
        }
      } catch (error) {
        // console.error('Error validating secure domain:', error);
        setIsValid(false);
      } finally {
        setLoading(false);
      }
    };

    validateAndSetDomain();
  }, [domainUUID, selectedDomain, getRealDomainId, getDomainUUID]);

  // Rediriger vers une URL sécurisée
  const getSecureDomainPath = useCallback((basePath, domainId = null) => {
    const targetDomainId = domainId || selectedDomain?.id;
    if (!targetDomainId) return basePath;

    const uuid = getDomainUUID(targetDomainId);
    return `${basePath}/${uuid}`;
  }, [selectedDomain, getDomainUUID]);

  // Forcer la redirection vers une URL sécurisée
  const redirectToSecurePath = useCallback((basePath, domainId = null) => {
    const securePath = getSecureDomainPath(basePath, domainId);
    navigate(securePath);
  }, [navigate, getSecureDomainPath]);

  return {
    secureDomainId,
    domainUUID,
    isValid,
    loading,
    getRealDomainId,
    getDomainUUID,
    getSecureDomainPath,
    redirectToSecurePath
  };
};